/**
 * Друзья и таблица среди них (ТЗ FR-GM-07, FR-LB-01).
 *
 * Список спрашивается при открытии экрана и после каждого изменения: он
 * меняется действиями человека, а не сам собой, и держать его копию,
 * расходящуюся с сервером, незачем.
 */

import { useCallback, useEffect, useState } from 'react';
import type { AddFriendOutcome, FriendsView, LeaderboardBoard } from '@type2x/feature-leaderboard';

import type { Runtime } from '../runtime.js';

export interface FriendsAccess {
  /** Список друзей и свой код; `null` — сервер не ответил. */
  readonly view: FriendsView | null;
  /** Таблица среди друзей; `null` — сервер не ответил. */
  readonly board: LeaderboardBoard | null;
  readonly add: (code: string) => Promise<AddFriendOutcome>;
  readonly remove: (linkId: string) => Promise<boolean>;
}

export function useFriends(runtime: Runtime, language: string): FriendsAccess {
  const [view, setView] = useState<FriendsView | null>(null);
  const [board, setBoard] = useState<LeaderboardBoard | null>(null);
  const [asked, setAsked] = useState(0);

  useEffect(() => {
    let current = true;

    void Promise.all([runtime.friends.view(), runtime.friends.board(language)])
      .then(([friends, table]) => {
        if (!current) return;
        setView(friends);
        setBoard(table);
      })
      .catch(() => {
        if (!current) return;
        setView(null);
        setBoard(null);
      });

    return () => {
      current = false;
    };
  }, [runtime, language, asked]);

  const add = useCallback(
    async (code: string): Promise<AddFriendOutcome> => {
      const outcome = await runtime.friends.add(code);
      if (outcome === 'added') setAsked((count) => count + 1);
      return outcome;
    },
    [runtime],
  );

  const remove = useCallback(
    async (linkId: string): Promise<boolean> => {
      const removed = await runtime.friends.remove(linkId);
      if (removed) setAsked((count) => count + 1);
      return removed;
    },
    [runtime],
  );

  return { view, board, add, remove };
}
